<form [formGroup]="receiptForm" (ngSubmit)="onSubmit()" class="form-container">
  <!-- Receipt Header -->
  <div class="_disclaimer-container">
    <div class="_disclaimer-icon">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
    </div>
    <div class="_disclaimer-msg">
      Due to inherent limitations, OCR functionality may not capture all items accurately; hence, manual review and validation are required.
    </div>
  </div>

  <!-- Overall confidence banner -->
  @if (getOverallConfidence() !== null) {
    <div class="confidence-banner" [ngClass]="getConfidenceClass(getOverallConfidence())">
      <i class="fa fa-shield"></i>
      <span>AI Extraction Confidence: <strong>{{ confidencePct(getOverallConfidence()) }}</strong></span>
      @if (isLowConfidence(getOverallConfidence())) {
        <span class="confidence-banner-hint"> — Low confidence detected. Fields with <span class="conf-dot confidence-low"></span> or <span class="conf-dot confidence-medium"></span> markers need manual review.</span>
      }
    </div>
  }

  <!-- AI Remarks & Findings -->
  @if (remarksArray.length > 0) {
    <div class="form-section remarks-panel">
      <h2 class="section-title"><i style=" color:#0061a9; font-size: 20px;">✦</i> 
        <span style=" color:#0061a9; font-size: 20px;">AI Generated Insights</span></h2>
      <ul class="remarks-list">
        @for (remark of remarksArray; track remark) {
          <li>{{ remark }}</li>
        }
      </ul>
    </div>
  }

  <div class="form-section">
    <h2 class="section-title">Receipt Information</h2>
    <div class="form-row">
      <div class="form-group">
        <label>Receipt ID <span class="conf-dot" [ngClass]="getConfidenceClass(getFieldConfidence('receiptId'))" [attr.title]="'AI confidence: ' + confidencePct(getFieldConfidence('receiptId'))"></span></label>
        <input type="text" class="form-control" [class.field-low-confidence]="isLowConfidence(getFieldConfidence('receiptId'))" formControlName="receiptId">
      </div>
      <div class="form-group">
        <label>Bill Date <span class="conf-dot" [ngClass]="getConfidenceClass(getFieldConfidence('billDate'))" [attr.title]="'AI confidence: ' + confidencePct(getFieldConfidence('billDate'))"></span></label>
        <input type="date" class="form-control" [class.field-low-confidence]="isLowConfidence(getFieldConfidence('billDate'))" formControlName="billDate">
      </div>
    </div>
  </div>

  <!-- Hospital Details -->

  <!-- Patient Details -->
  <div class="form-section" formGroupName="patientDetails">
    <h2 class="section-title">Patient Details</h2>
    <div class="form-row">
      <div class="form-group">
        <label>Age</label>
        <input type="number" class="form-control" formControlName="age">
      </div>
      <div class="form-group">
        <label>Gender</label>
        <select class="form-control" formControlName="gender">
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>
    </div>

    <!-- Admission Details -->
    <div formGroupName="admissionDetails">
      <h3 class="subsection-title">Admission Details</h3>
      <div class="form-row">
        <div class="form-group">
          <label>Admission Number <span class="conf-dot" [ngClass]="getConfidenceClass(getFieldConfidence('admissionNumber'))" [attr.title]="'AI confidence: ' + confidencePct(getFieldConfidence('admissionNumber'))"></span></label>
          <input type="text" class="form-control" [class.field-low-confidence]="isLowConfidence(getFieldConfidence('admissionNumber'))" formControlName="admissionNumber">
        </div>
        <div class="form-group">
          <label>Admission Date <span class="conf-dot" [ngClass]="getConfidenceClass(getFieldConfidence('admissionDate'))" [attr.title]="'AI confidence: ' + confidencePct(getFieldConfidence('admissionDate'))"></span></label>
          <input type="datetime-local" class="form-control" [class.field-low-confidence]="isLowConfidence(getFieldConfidence('admissionDate'))" formControlName="admissionDate">
        </div>
        <div class="form-group">
          <label>Discharge Date <span class="conf-dot" [ngClass]="getConfidenceClass(getFieldConfidence('dischargeDate'))" [attr.title]="'AI confidence: ' + confidencePct(getFieldConfidence('dischargeDate'))"></span></label>
          <input type="datetime-local" class="form-control" [class.field-low-confidence]="isLowConfidence(getFieldConfidence('dischargeDate'))" formControlName="dischargeDate">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Admitted Under Doctor</label>
          <input type="text" class="form-control" formControlName="admittedUnderDoctor">
        </div>
        <div class="form-group">
          <label>Room/Bed Number</label>
          <input type="text" class="form-control" formControlName="roomOrBedNumber">
        </div>
      </div>
    </div>
  </div>

  <!-- Charge Details -->
  <div class="form-section">
    <div class="section-header">
      <h2 class="section-title">Charge Details</h2>
      <div class="section-actions">
        <button type="button" class="icon-button" (click)="expandAllCategories()" title="Expand All">
          <i class="fa fa-expand"></i>
        </button>
        <button type="button" class="icon-button" (click)="collapseAllCategories()" title="Collapse All">
          <i class="fa fa-compress"></i>
        </button>
      </div>
    </div>

    <div formArrayName="chargeDetails">
      @for (category of chargeDetails.controls; track trackByCategory(catIndex, category); let catIndex = $index) {
        <div
          [formGroupName]="catIndex"
          class="charge-category">
          <div class="category-header" (click)="toggleCategory(catIndex)">
            <div class="category-title">
              <i class="fa" [class.fa-chevron-down]="!isCategoryCollapsed(catIndex)" [class.fa-chevron-right]="isCategoryCollapsed(catIndex)"></i>
              <div class="form-group category-name">
                <input type="text" class="form-control" formControlName="categoryName" placeholder="Category Name" (click)="$event.stopPropagation()">
              </div>
              @if (getCategoryConfidence(catIndex) !== null) {
                <span
                  class="conf-dot"
                  [ngClass]="getConfidenceClass(getCategoryConfidence(catIndex))"
                  [attr.title]="'Category confidence: ' + confidencePct(getCategoryConfidence(catIndex))"
                  (click)="$event.stopPropagation()">
                </span>
              }
            </div>
            <div class="category-actions">
              <div class="category-totals">
                <span class="total original">Total: {{ category.get('categorySubtotal').value | currency:'INR' }}</span>
                <span class="total approved">Approved: {{ category.get('approvedCategorySubtotal').value | currency:'INR' }}</span>
              </div>
              <button type="button" class="icon-button" (click)="confirmDeleteCategory(catIndex, $event)" title="Remove Category">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
          <div class="category-content" [class.collapsed]="isCategoryCollapsed(catIndex)">
            <div class="line-items-container" formArrayName="lineItems">
              <!-- Line Items Header -->
              <div class="line-item header">
                <div>Description</div>
                <div>Item Date</div>
                <div>Quantity</div>
                <div>Unit Price</div>
                <div>Discount</div>
                <div>Amount</div>
                <div>Approved</div>
                <div></div>
              </div>
              <!-- Line Items -->
              @for (item of category.get('lineItems').controls; track trackByLineItem(itemIndex, item); let itemIndex = $index) {
                <div
                  [formGroupName]="itemIndex"
                  class="line-item"
                  [ngClass]="getLineItemNgClass(item.get('class').value, catIndex, itemIndex)"
                  [attr.title]="getLineItemConfidence(catIndex, itemIndex) !== null ? 'AI confidence: ' + confidencePct(getLineItemConfidence(catIndex, itemIndex)) : null">
                  <input type="text" class="form-control" formControlName="itemDescription" placeholder="Description">
                  <input type="date" class="form-control" formControlName="itemDate" placeholder="Date">
                  <input type="number" class="form-control" formControlName="quantity" min="1" step="1">
                  <input type="number" class="form-control" formControlName="unitPrice" min="0" step="0.01">
                  <input type="number" class="form-control" formControlName="discount" min="0" step="0.01">
                  <input type="number" class="form-control" formControlName="amount" readonly>
                  <input type="number" class="form-control" formControlName="approvedAmount" min="0" step="0.01">
                  <button type="button" class="icon-button" (click)="confirmDeleteLineItem(catIndex, itemIndex)" title="Remove Item">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              }
            </div>
            <!-- Category Summary -->
            <div class="category-summary">
              <div class="summary-row">
                <span>Category Total:</span>
                <span class="total original">{{ category.get('categorySubtotal').value | currency:'INR' }}</span>
              </div>
              <div class="summary-row">
                <span>Approved Total:</span>
                <span class="total approved">{{ category.get('approvedCategorySubtotal').value | currency:'INR' }}</span>
              </div>
            </div>
            <button type="button" class="btn btn-add" (click)="addLineItem(catIndex)">
              <i class="fa fa-plus"></i>
              Add Item
            </button>
          </div>
        </div>
      }

      <!-- Add Category Button -->
      <div class="add-category-container">
        <button type="button" class="btn btn-add" (click)="addChargeCategory()">
          <i class="fa fa-plus"></i>
          Add Category
        </button>
      </div>
    </div>
  </div>

  <!-- Billing Summary -->
  <div class="form-section" formGroupName="billingSummary">
    <h2 class="section-title">Billing Summary</h2>

    <div class="category-summary">
      <div class="summary-row">
        <span>Gross Amount:</span>
        <span>{{ receiptForm.get('billingSummary.grossAmount').value | currency:'INR' }}</span>
      </div>
      <div class="summary-row">
        <span>Approved Gross:</span>
        <span>{{ receiptForm.get('billingSummary.approvedGrossAmount').value | currency:'INR' }}</span>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Discount</label>
          <input type="number" class="form-control" formControlName="totalDiscount" readonly>
        </div>
        <div class="form-group">
          <label>Service Charge</label>
          <input type="number" class="form-control" formControlName="serviceCharge" min="0">
        </div>
        <div class="form-group">
          <label>Advance Paid</label>
          <input type="number" class="form-control" formControlName="advancePaid" min="0">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Amount After Discount</label>
          <input type="number" class="form-control" formControlName="amountAfterDiscount" readonly>
        </div>
        <div class="form-group">
          <label>Refunded Amount</label>
          <input type="number" class="form-control" formControlName="refundedAmount" readonly>
        </div>
        <div class="form-group">
          <label>Concession</label>
          <input type="number" class="form-control" formControlName="concession" min="0">
        </div>
        <div class="form-group">
          <label>Round Off</label>
          <input type="number" class="form-control" formControlName="roundOff">
        </div>
      </div>

      <div class="summary-row">
        <span>Net Payable:</span>
        <span>{{ receiptForm.get('billingSummary.netPayableAmount').value | currency:'INR' }}</span>
      </div>
      <div class="summary-row">
        <span>Approved Net Payable:</span>
        <span>{{ receiptForm.get('billingSummary.approvedNetPayableAmount').value | currency:'INR' }}</span>
      </div>

      <div class="form-row">
        <div class="form-group full-width">
          <label>Amount in Words</label>
          <input type="text" class="form-control" formControlName="amountInWords" readonly>
        </div>
      </div>
    </div>
  </div>

  <!-- Insurance Details -->
  <div class="form-section" formGroupName="insuranceDetails">
    <h2 class="section-title">Insurance Details</h2>
    <div class="form-row">
      <div class="form-group full-width">
        <label>Insurance Note</label>
        <textarea class="form-control" formControlName="note" rows="3"></textarea>
      </div>
    </div>
  </div>

  <!-- Financial Validation Gaps -->
  @if (financialGaps.length > 0) {
    <div class="form-section gaps-panel">
      <h2 class="section-title"><i class="fa fa-exclamation-triangle"></i> Financial Validation Gaps</h2>
      <p class="gaps-description">
        Discrepancies between AI-reported values and totals calculated from line items. These may indicate OCR errors or missing items — please verify.
      </p>
      <div class="gaps-table">
        <div class="gaps-row gaps-header">
          <span>Item</span>
          <span>Calculated from lines</span>
          <span>AI reported</span>
          <span>Difference</span>
        </div>
        @for (gap of financialGaps; track gap.label) {
          <div class="gaps-row">
            <span>{{ gap.label }}</span>
            <span>{{ gap.computed | currency:'INR' }}</span>
            <span>{{ gap.reported | currency:'INR' }}</span>
            <span [class.diff-positive]="gap.diff > 0" [class.diff-negative]="gap.diff < 0">
              {{ gap.diff > 0 ? '+' : '' }}{{ gap.diff | currency:'INR' }}
            </span>
          </div>
        }
      </div>
    </div>
  }

<!-- Form Actions -->
<div class="form-actions">
  <button type="submit" class="btn btn-primary" [disabled]="!receiptForm.valid">
    <i class="fa fa-save"></i>
    Fill
  </button>
</div>
</form>
